import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { TableConstants } from '../../../constants/table.constant';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { thirdPartiesFilterPredicate } from './third-parties-filter-predicate';
import { ThirdPartyAssignmentItem } from '../../../interfaces/third-party-assignement-item.interface';
import { ThirdPartyStatus } from '../../../interfaces/third-party-status.interface';

export interface ThirdPartiesDialogData {
  thirdParties: ThirdPartyAssignmentItem[];
  returnSelectedOnly?: boolean;
}

@Component({
  selector: 'si-third-parties-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatTableModule, CommonModule, ReactiveFormsModule, MatCheckboxModule, MatInputModule, MatPaginatorModule, MatIconModule],
  templateUrl: './third-parties-dialog.component.html',
  styleUrl: './third-parties-dialog.component.scss'
})
export class ThirdPartiesDialogComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  private _destroy$ = new Subject<void>();

  public TableConstants = TableConstants;

  fb = inject(FormBuilder);
  dialogRef = inject(MatDialogRef<ThirdPartiesDialogComponent>);
  data = inject<ThirdPartiesDialogData>(MAT_DIALOG_DATA);
  form = this.fb.array(
    this.data.thirdParties?.map(
      ({ organisationName, statusID, thirdPartyAssignmentID, thirdPartyID }) => this.fb.group({
        value: new FormControl(statusID === ThirdPartyStatus.Active),
        organisationName: new FormControl({ disabled: true, value: organisationName }),
        thirdPartyID: new FormControl({ disabled: true, value: thirdPartyID }),
        thirdPartyAssignmentID: new FormControl({ disabled: true, value: thirdPartyAssignmentID }),
      })
    )
  );

  columns = ['thirdParty', 'actions'];
  filter: FormControl = new FormControl('');
  dataSource = new MatTableDataSource<ThirdPartyAssignmentItem>();

  ngOnInit() {
    this.dataSource = new MatTableDataSource(this.data.thirdParties);
    this.dataSource.paginator = this.paginator;
    this.dataSource.filterPredicate = thirdPartiesFilterPredicate;

    this.addSubscription();
  }

  public getFormArrayControl(element: string) {
    return this.form.controls
      .find((control) => control.get('organisationName').value === element)
      .get('value');
  }

  addSubscription() {
    this.filter.valueChanges.pipe(
      takeUntil(this._destroy$),
      debounceTime(500),
      distinctUntilChanged(),
    ).subscribe(value => {
      this.applyFilter(value);
    });
  }

  applyFilter(value: string) {
    this.dataSource.filter = value.trim().toLowerCase();
    this.dataSource.paginator.firstPage();
  }

  handleConfirm() {
    const selectedEntries = this.form.getRawValue()
      .filter(({ organisationName }) => this.data.returnSelectedOnly ? this.getFormArrayControl(organisationName).value : this.getFormArrayControl(organisationName).dirty)
      .map((entry) => ({
        ...entry,
        statusID: entry.value ? ThirdPartyStatus.Active : ThirdPartyStatus.Inactive,
      }));

    this.dialogRef.close(selectedEntries);
  }

  handleClose() {
    this.dialogRef.close();
  }
}
